import type { ReactNode } from "react";

import {
	PopoverSelect,
	type PopoverSelectOption,
} from "#/components/ui/popover-select.tsx";
import { cn } from "#/lib/utils";

export type OrderFormSelectOption<T extends string> = PopoverSelectOption<T>;

type OrderFormSelectProps<T extends string> = {
	ariaLabel: string;
	contentClassName?: string;
	itemClassName?: string;
	matchTriggerWidth?: boolean;
	onValueChange: (nextValue: T) => void;
	options: ReadonlyArray<OrderFormSelectOption<T>>;
	triggerClassName?: string;
	triggerLabel?: ReactNode;
	value: T;
};

const triggerBaseClassName =
	"w-full justify-start gap-2 rounded-[10px] border border-trade-border-subtle bg-trade-surface text-label1-bold text-trade-text shadow-none transition-[border-color,color,box-shadow,background-color] hover:border-trade-border-hover [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:text-trade-text [&_[data-slot=select-value]]:transition-colors [&_[data-slot=popover-trigger-icon]]:ml-auto [&_[data-slot=popover-trigger-icon]]:text-trade-text-secondary [&_[data-slot=popover-trigger-icon]]:opacity-100 data-[state=open]:border-[var(--trade-accent)] data-[state=open]:shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-theme-primary-normal)_20%,transparent)] data-[state=open]:[&_[data-slot=select-value]]:text-[var(--trade-accent)] data-[state=open]:[&_[data-slot=popover-trigger-icon]]:text-[var(--trade-accent)]";

const contentBaseClassName =
	"rounded-xl border border-trade-border-subtle shadow-[var(--trade-shadow-popover)] dark:border-[var(--color-theme-line-default)] dark:bg-[var(--color-theme-bg-elevated)] [&_[data-slot=select-viewport]]:p-0";

const itemBaseClassName =
	"min-h-[34px] rounded-lg text-label1-bold text-trade-text [&_[data-slot=select-item-indicator]]:text-[var(--trade-accent)]";

export function OrderFormSelect<T extends string>({
	ariaLabel,
	contentClassName,
	itemClassName,
	matchTriggerWidth = true,
	onValueChange,
	options,
	triggerClassName,
	triggerLabel,
	value,
}: OrderFormSelectProps<T>) {
	return (
		<PopoverSelect
			ariaLabel={ariaLabel}
			align="end"
			contentClassName={cn(contentBaseClassName, contentClassName)}
			itemClassName={cn(itemBaseClassName, itemClassName)}
			matchTriggerWidth={matchTriggerWidth}
			onValueChange={onValueChange}
			options={options}
			sideOffset={2}
			triggerClassName={cn(
				triggerBaseClassName,
				triggerLabel && "[&_[data-slot=select-value]]:text-right",
				triggerClassName,
			)}
			triggerLabel={triggerLabel}
			value={value}
		/>
	);
}
